<template>
  <h2>{{ t('billing') }}</h2>

  <div class="flex flex-wrap items-end gap-6">
    <TaxProcedureSelect
      v-if="$can('update', subject('Booking', booking), 'taxProcedure')"
      :model-value="taxProcedure"
      :vat-id="booking.customerData?.vatId"
      :tax-country="booking.taxCountry"
      @update:model-value="handleTaxProcedureUpdate"
    />
    <InvoicingTypeSelect
      v-if="$can('update', subject('Booking', booking), 'invoicingType')"
      v-model="invoicingType"
      class="min-w-[16rem]"
      :allowed-invoicing-types="booking.allowedInvoicingTypes"
    />
    <CheckboxField
      name="hasPeriodicInvoicing"
      :label="t('periodicInvoicing')"
      :disabled="isLongTermPricing"
    />
    <CheckboxField
      name="collectDeposit"
      :label="t('collectDeposit')"
      :disabled="depositAmount <= 0"
    />
    <CVButton
      v-if="canConvertToLongTerm"
      variant="secondary"
      type="button"
      @click.prevent="isConvertOpen = true"
    >
      {{ t('convertToLongTerm') }}
    </CVButton>
  </div>

  <ConvertToLongTermModal
    v-model="isConvertOpen"
    :booking="booking"
    :is-submitting="isConverting"
    @convert="handleConvert"
  />

  <div v-if="hasChanged" class="flex justify-end gap-4">
    <CVButton
      variant="default"
      data-testid="billing-settings-save"
      :is-loading="isSaving"
      @click.prevent="onSubmit"
      >{{ t('save') }}</CVButton
    >
    <CVButton variant="secondary" :disabled="isSaving" @click.prevent="onCancel">{{
      t('cancel')
    }}</CVButton>
  </div>
</template>

<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { cloneDeep } from 'lodash';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import CVButton from '@/components/buttons/CVButton.vue';
import CheckboxField from '@/components/CheckboxField.vue';
import TaxProcedureSelect from '@/components/TaxProcedureSelect.vue';
import InvoicingTypeSelect from '@/components/InvoicingTypeSelect.vue';
import { subject } from '@casl/ability';
import { TaxProcedure, PriceRecalculationMode } from '@carvia/ros-client-types';
import { useSuggestedTaxProcedure } from '@/hooks/use-suggested-tax-procedure';
import { useTaxLegalEntity } from '@/hooks/use-tax-legal-entity';
import { resolveHandoverCountry } from '@/utils/resolve-tax-country';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import { useConvertToLongTerm, useUpdateBooking } from '@/queries/use-bookings.ts';
import { isLongTermPricing as isLongTermPricingQuote } from '@/utils/pricing.utils';
import { isEligibleForLongTermConversion } from '@/utils/long-term-conversion.utils';
import ConvertToLongTermModal from '@/components/booking-forms/ConvertToLongTermModal.vue';

const props = defineProps<{
  booking: Booking;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t } = useI18n();

const { confirmAndSave, isBusy } = usePriceChangeConfirmation();
const { isPending: isSavingOtherFields, mutateAsync: updateBooking } = useUpdateBooking();
const { isPending: isConverting, mutateAsync: convertToLongTerm } = useConvertToLongTerm();
const isConvertOpen = ref(false);
const canConvertToLongTerm = computed(() => isEligibleForLongTermConversion(props.booking));

const isSaving = computed(() => isBusy.value || isSavingOtherFields.value);

const depositAmount = computed(() => props.booking.priceCalculation?.deposit ?? 0);
const isLongTermPricing = computed(
  () => !!props.booking.priceCalculation && isLongTermPricingQuote(props.booking.priceCalculation),
);

const taxLegalEntity = useTaxLegalEntity(() => props.booking.legalEntity?.id);

const { taxProcedure, setTaxProcedure } = useSuggestedTaxProcedure({
  mode: 'edit',
  startDate: () => props.booking.startDate,
  endDate: () => props.booking.endDate,
  vatId: () => props.booking.customerData?.vatId,
  country: () => props.booking.customerData?.country,
  handoverCountry: () => resolveHandoverCountry(props.booking),
  legalEntity: taxLegalEntity,
  initialTaxProcedure: () => props.booking.taxProcedure,
});

const handleTaxProcedureUpdate = (value: TaxProcedure) => {
  setTaxProcedure(value, true);
};

const invoicingType = ref(props.booking.invoicingType);

const {
  handleSubmit,
  values: formValues,
  setValues,
} = useForm({
  initialValues: {
    hasPeriodicInvoicing: props.booking.hasPeriodicInvoicing,
    collectDeposit: props.booking.collectDeposit ?? false,
  },
  validationSchema: yup.object({
    hasPeriodicInvoicing: yup.boolean(),
    collectDeposit: yup.boolean(),
  }),
});

const unchangedValues = ref({
  hasPeriodicInvoicing: props.booking.hasPeriodicInvoicing,
  collectDeposit: props.booking.collectDeposit ?? false,
  taxProcedure: props.booking.taxProcedure ?? TaxProcedure.REGULAR,
  invoicingType: props.booking.invoicingType,
});

const currentValues = computed(() => ({
  hasPeriodicInvoicing: formValues.hasPeriodicInvoicing,
  collectDeposit: formValues.collectDeposit,
  taxProcedure: taxProcedure.value,
  invoicingType: invoicingType.value,
}));

const onCancel = () => {
  setValues({
    hasPeriodicInvoicing: props.booking.hasPeriodicInvoicing,
    collectDeposit: props.booking.collectDeposit ?? false,
  });
  setTaxProcedure(props.booking.taxProcedure ?? TaxProcedure.REGULAR, false);
  invoicingType.value = props.booking.invoicingType;
  unchangedValues.value = {
    hasPeriodicInvoicing: props.booking.hasPeriodicInvoicing,
    collectDeposit: props.booking.collectDeposit ?? false,
    taxProcedure: props.booking.taxProcedure ?? TaxProcedure.REGULAR,
    invoicingType: props.booking.invoicingType,
  };
};

const onSubmit = handleSubmit(async (values) => {
  const taxProcedureChanged = taxProcedure.value !== unchangedValues.value.taxProcedure;
  const bookingUpdate = {
    hasPeriodicInvoicing: values.hasPeriodicInvoicing,
    collectDeposit: values.collectDeposit,
    taxProcedure: taxProcedure.value,
    // Only send a changed type, so unrelated saves never re-validate the stored one.
    ...(invoicingType.value !== unchangedValues.value.invoicingType
      ? { invoicingType: invoicingType.value }
      : {}),
  };

  if (taxProcedureChanged) {
    const { status } = await confirmAndSave({
      booking: props.booking,
      updateBookingDto: {
        ...bookingUpdate,
        pricing: {
          priceRecalculationMode: PriceRecalculationMode.RECALCULATE,
        },
      },
      allowKeepOldPrice: true,
    });
    if (status !== 'saved') {
      return;
    }
  } else {
    await updateBooking({
      id: props.booking.id,
      booking: bookingUpdate,
    });
  }

  unchangedValues.value = cloneDeep(currentValues.value);
});

// The invoicing type also changes server-side when the agency is linked or removed.
watch(
  () => props.booking.invoicingType,
  (value) => {
    invoicingType.value = value;
    unchangedValues.value = { ...unchangedValues.value, invoicingType: value };
  },
);

const hasChanged = useFormHasChanged(unchangedValues, currentValues);
watch(hasChanged, () => emit('hasChanged', hasChanged.value));

const handleConvert = async (payload: {
  quoteStartDate: string;
  customTotalBasePrice: number;
  bookedExtras: { vehicleExtraId?: string; vehicleExtraOptionId?: string }[];
  customBookedExtras: unknown;
  invoicePickupTransfer?: boolean;
  invoiceDropoffTransfer?: boolean;
}) => {
  await convertToLongTerm({ id: props.booking.id, dto: payload });
  isConvertOpen.value = false;
};
</script>

<i18n lang="json">
{
  "en": {
    "billing": "Billing",
    "periodicInvoicing": "Manual Invoicing",
    "collectDeposit": "Request deposit claim",
    "convertToLongTerm": "Convert to long-term"
  },
  "de": {
    "billing": "Abrechnung",
    "periodicInvoicing": "Manuelle Abrechnung",
    "collectDeposit": "Kaution einfordern",
    "convertToLongTerm": "In Langzeit umwandeln"
  },
  "es": {
    "billing": "Facturación",
    "periodicInvoicing": "Facturación manual",
    "collectDeposit": "Reclamar depósito",
    "convertToLongTerm": "Convertir a larga duración"
  }
}
</i18n>
